import { BaseLineMessage } from '@/app/components/icons/BaseLineMessage';
import { useStore } from '@/app/providers/store';
import { CommunityEnum } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useRouter } from '@/i18n/routing';
import { useNotificationTime } from '../hook/use-notification-time';
import { useRenderFormat } from '../hook/use-render-format';
import type { UserSysNotification } from '../types';

interface RenderListProps {
  data: UserSysNotification;
  onNotificationClick: (id: string) => void;
  className?: string;
  setOpenSysNotificationId: (id: string) => void;
}

export const SystemNotificationItem = ({
  onNotificationClick,
  className,
  data,
  setOpenSysNotificationId,
}: RenderListProps) => {
  const { notification, is_read: isRead } = data;
  const { id, title, content, publish_at: publishTime, action } = notification ?? {};
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));
  const router = useRouter();
  const { renderHtmlContent } = useRenderFormat();
  const relativeTimeText = useNotificationTime(publishTime ?? '');

  const handleClick = (e: React.MouseEvent) => {
    onNotificationClick(id ?? '');
    reportEvent('notification_click', {
      id: id ?? '',
      title: title || '',
      type: 'platform_messages',
    });

    const actionType = action?.type;
    const actionTarget = action?.target;

    if (actionType === 'modal' && actionTarget) {
      switch (actionTarget) {
        case 'ranking':
          break;
        case 'profile':
          openOrRedirect(`/profile/${userInfo?.id}?obj_type=${CommunityEnum.PostReferences}`);
          return;
      }
    }

    if (actionType === 'link' && actionTarget === 'agent_invite_success') {
      router.push('/project');
      return;
    }

    // 检查点击的是否是 a 标签或其子元素
    // 如果是 a 标签点击，执行跳转，不再显示弹窗
    const target = e.target as HTMLElement;
    const isLinkClick = target.closest('a');

    if (isLinkClick) {
      return;
    }
    setOpenSysNotificationId(id ?? '');
  };

  return (
    <div
      className={cn('flex cursor-pointer gap-3 rounded-8 px-2 py-3 hover:bg-system-white08', className)}
      onClick={handleClick}
    >
      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-system-white12">
        <BaseLineMessage className="text-white text-xl" />
        {!isRead && <div className="absolute top-0 right-0 h-2 w-2 rounded-full bg-system-error1" />}
      </div>
      <div className="text-sm">
        <div className={cn('font-semibold text-system-white')}>{renderHtmlContent(title)}</div>
        <div className="my-2 line-clamp-2 break-all text-system-white64 [-webkit-box-orient:vertical] [display:-webkit-box] group-hover:text-system-white">
          {renderHtmlContent(content)}
        </div>
        <div className="text-system-white48 text-xs">{relativeTimeText}</div>
      </div>
    </div>
  );
};
